// Languages of the PUBLIC website (the pages outside the login) and the rules
// that choose one for a visitor.
//
// CommonJS and dependency-free on purpose: the same file runs in the browser
// bundle, in the build scripts (plain node), in the tests and in the
// Cloudflare Worker that answers "/" (lang-worker/). One list, so the
// language menu, the prerendered pages, the sitemap and the redirect can
// never disagree.
//
// The APP (every screen behind the login, /auth, payment and tracking links)
// keeps its own languages, APP_LANGUAGES: visiting /fr/ never changes them.
//
// URLs: Spanish keeps the addresses it always had (/funcionalidades); every
// other language lives under its prefix (/fr/funcionalidades, /fr/ for the
// home). /es/… also works, as an alias of the Spanish page.

// The 24 official EU languages, plus Catalan (not an EU language, but the
// site and the app already offered it). Native names, as the menu shows them.
// `locale` formats numbers and dates; `og` is the Open Graph locale.
const LANGUAGES = [
  { code: 'es', name: 'Español', locale: 'es-ES', og: 'es_ES' },
  { code: 'ca', name: 'Català', locale: 'ca-ES', og: 'ca_ES' },
  { code: 'en', name: 'English', locale: 'en-GB', og: 'en_GB' },
  { code: 'bg', name: 'Български', locale: 'bg-BG', og: 'bg_BG' },
  { code: 'cs', name: 'Čeština', locale: 'cs-CZ', og: 'cs_CZ' },
  { code: 'da', name: 'Dansk', locale: 'da-DK', og: 'da_DK' },
  { code: 'de', name: 'Deutsch', locale: 'de-DE', og: 'de_DE' },
  { code: 'et', name: 'Eesti', locale: 'et-EE', og: 'et_EE' },
  { code: 'el', name: 'Ελληνικά', locale: 'el-GR', og: 'el_GR' },
  { code: 'fr', name: 'Français', locale: 'fr-FR', og: 'fr_FR' },
  { code: 'ga', name: 'Gaeilge', locale: 'ga-IE', og: 'ga_IE' },
  { code: 'hr', name: 'Hrvatski', locale: 'hr-HR', og: 'hr_HR' },
  { code: 'it', name: 'Italiano', locale: 'it-IT', og: 'it_IT' },
  { code: 'lv', name: 'Latviešu', locale: 'lv-LV', og: 'lv_LV' },
  { code: 'lt', name: 'Lietuvių', locale: 'lt-LT', og: 'lt_LT' },
  { code: 'hu', name: 'Magyar', locale: 'hu-HU', og: 'hu_HU' },
  { code: 'mt', name: 'Malti', locale: 'mt-MT', og: 'mt_MT' },
  { code: 'nl', name: 'Nederlands', locale: 'nl-NL', og: 'nl_NL' },
  { code: 'pl', name: 'Polski', locale: 'pl-PL', og: 'pl_PL' },
  { code: 'pt', name: 'Português', locale: 'pt-PT', og: 'pt_PT' },
  { code: 'ro', name: 'Română', locale: 'ro-RO', og: 'ro_RO' },
  { code: 'sk', name: 'Slovenčina', locale: 'sk-SK', og: 'sk_SK' },
  { code: 'sl', name: 'Slovenščina', locale: 'sl-SI', og: 'sl_SI' },
  { code: 'fi', name: 'Suomi', locale: 'fi-FI', og: 'fi_FI' },
  { code: 'sv', name: 'Svenska', locale: 'sv-SE', og: 'sv_SE' },
];

const CODES = LANGUAGES.map((l) => l.code);
const DEFAULT_LANGUAGE = 'es';
// Languages the app screens are translated into (lib/i18n.js).
const APP_LANGUAGES = ['es', 'en', 'ca'];

// The visitor's explicit choice, set by the language menu. One year.
const COOKIE = 'zb_lang';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

// Last resort when neither a saved choice nor the browser names a language
// the site has: the visitor's country (Cloudflare's CF-IPCountry, only
// available to the Worker). Countries with several languages get the one most
// of their visitors read; Ireland and Malta get English (Irish and Maltese are
// official, but English is what most of their visitors browse in — anyone
// whose browser asks for ga or mt still gets it, at the step before).
const COUNTRY_LANGUAGE = {
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es',
  BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es', GQ: 'es',
  AD: 'ca',
  GB: 'en', IE: 'en', MT: 'en', GI: 'en', US: 'en', CA: 'en', AU: 'en', NZ: 'en', IN: 'en', PK: 'en', ZA: 'en',
  NG: 'en', SG: 'en', PH: 'en',
  FR: 'fr', MC: 'fr', LU: 'fr',
  DE: 'de', AT: 'de', CH: 'de', LI: 'de',
  IT: 'it', SM: 'it', VA: 'it',
  PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt', CV: 'pt',
  NL: 'nl', BE: 'nl', SR: 'nl',
  PL: 'pl', RO: 'ro', MD: 'ro', CZ: 'cs', SK: 'sk', HU: 'hu', BG: 'bg', HR: 'hr', SI: 'sl',
  GR: 'el', CY: 'el', SE: 'sv', DK: 'da', GL: 'da', FO: 'da', FI: 'fi', EE: 'et', LV: 'lv', LT: 'lt',
};

// Search engines, link-preview fetchers and other robots: never redirected,
// so they always read the address they asked for. Named one by one (a bare
// "bot" would also catch phones such as "CUBOT X30"), plus the "+http://…"
// contact address most robots put in their user agent.
const BOT = /googlebot|google-inspectiontool|googleother|google-extended|storebot-google|adsbot|mediapartners-google|apis-google|feedfetcher|bingbot|bingpreview|msnbot|adidxbot|yandex|baiduspider|duckduckbot|duckassistbot|slurp|applebot|petalbot|seznambot|sogou|exabot|ahrefsbot|semrushbot|mj12bot|dotbot|rogerbot|screaming frog|gptbot|chatgpt-user|oai-searchbot|claudebot|claude-web|anthropic-ai|ccbot|perplexitybot|amazonbot|bytespider|ia_archiver|archive\.org_bot|facebookexternalhit|facebot|meta-externalagent|twitterbot|linkedinbot|slackbot|discordbot|telegrambot|whatsapp|skypeuripreview|pinterestbot|redditbot|embedly|quora link preview|vkshare|w3c_validator|chrome-lighthouse|headlesschrome|crawler|spider|\+https?:\/\//i;

// The Android app (Capacitor WebView, "; wv)") and other in-app browsers:
// they open "/" as an app, never redirected.
const APP_WEBVIEW = /; wv\)|zbillingapp/i;

// Login links (password recovery, sign-up confirmation…) carry their token in
// the address; they must reach the page they were sent to, untouched.
const AUTH_PARAMS = /access_token|refresh_token|token_hash|[?&#]code=|[?&#]type=(recovery|signup|invite|magiclink|email_change)|error_description/i;

const byCode = new Map(LANGUAGES.map((l) => [l.code, l]));

// 'fr-FR' → 'fr'; '' / null → ''.
function baseCode(code) {
  return String(code || '').trim().toLowerCase().split(/[-_]/)[0];
}

function isSiteLanguage(code) {
  return byCode.has(baseCode(code));
}

function isAppLanguage(code) {
  return APP_LANGUAGES.includes(baseCode(code));
}

function languageInfo(code) {
  return byCode.get(baseCode(code)) || byCode.get(DEFAULT_LANGUAGE);
}

// "fr-CH, fr;q=0.9, en;q=0.8, *;q=0.5" → ['fr-CH', 'fr', 'en'] by preference.
// q=0 means "not this one"; "*" names nothing.
function parseAcceptLanguage(header) {
  return String(header || '')
    .split(',')
    .map((part, i) => {
      const [tag, ...params] = part.trim().split(';');
      const qp = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
      const q = qp ? Number(qp.slice(2)) : 1;
      return { tag: tag.trim(), q: Number.isFinite(q) ? q : 0, i };
    })
    .filter((x) => x.tag && x.tag !== '*' && x.q > 0)
    .sort((a, b) => b.q - a.q || a.i - b.i)
    .map((x) => x.tag);
}

// First language of the browser's list that the site has, or null.
function fromBrowserLanguages(tags) {
  for (const tag of tags || []) {
    const code = baseCode(tag);
    if (byCode.has(code)) return code;
  }
  return null;
}

function fromAcceptLanguage(header) {
  return fromBrowserLanguages(parseAcceptLanguage(header));
}

function fromCountry(country) {
  return COUNTRY_LANGUAGE[String(country || '').trim().toUpperCase()] || null;
}

// Value of one cookie from a Cookie header / document.cookie.
function readCookie(cookieHeader, name = COOKIE) {
  for (const part of String(cookieHeader || '').split(';')) {
    const i = part.indexOf('=');
    if (i > -1 && part.slice(0, i).trim() === name) {
      try { return decodeURIComponent(part.slice(i + 1).trim()); } catch (e) { return part.slice(i + 1).trim(); }
    }
  }
  return null;
}

function isBot(userAgent) {
  return BOT.test(String(userAgent || ''));
}

function isAppWebView(userAgent) {
  return APP_WEBVIEW.test(String(userAgent || ''));
}

function hasAuthParams(search, hash) {
  return AUTH_PARAMS.test(`${search || ''}${hash || ''}`);
}

// The language for a visitor, in the agreed order:
//   1. the language they chose before (the zb_lang cookie, written only when
//      someone picks a language — never guessed),
//   2. (the language in the URL — never overridden; see rootRedirect)
//   3. the first site language of the browser's list (`browser`: the
//      browser's navigator.languages; `acceptLanguage`: the request header),
//   4. the country (CF-IPCountry),
//   5. Spanish.
// The app's own saved language (localStorage "zbilling_lang") is NOT a
// choice: the app writes it on every first visit, from the browser.
function pickLanguage({ cookie, browser, acceptLanguage, country } = {}) {
  if (isSiteLanguage(cookie)) return { lang: baseCode(cookie), source: 'cookie' };
  const fromList = browser ? fromBrowserLanguages(browser) : fromAcceptLanguage(acceptLanguage);
  if (fromList) return { lang: fromList, source: 'browser' };
  const fromIp = fromCountry(country);
  if (fromIp) return { lang: fromIp, source: 'country' };
  return { lang: DEFAULT_LANGUAGE, source: 'default' };
}

// Where "/" sends a visitor: { location } for a temporary (302) redirect to
// that language's home, or { location: null } to show the Spanish home that
// lives at "/". Only "/" ever redirects; any other address — including every
// /xx/… address — is shown as asked.
function rootRedirect({ pathname, search = '', hash = '', userAgent, ...signals } = {}) {
  const none = (reason, extra = {}) => ({ location: null, reason, ...extra });
  if (pathname !== '/') return none('not-root');
  if (isBot(userAgent)) return none('bot');
  if (isAppWebView(userAgent)) return none('app');
  if (hasAuthParams(search, hash)) return none('auth-link');
  const { lang, source } = pickLanguage(signals);
  if (lang === DEFAULT_LANGUAGE) return none('spanish', { lang, source });
  return { location: `/${lang}/${search || ''}${hash || ''}`, lang, source, reason: 'redirect' };
}

// '/fr/funcionalidades' → { lang: 'fr', path: '/funcionalidades' }
// '/fr' or '/fr/'       → { lang: 'fr', path: '/' }
// '/es/x'               → { lang: 'es', path: '/x' }  (alias of /x)
// '/funcionalidades'    → { lang: null, path: '/funcionalidades' }
function splitLocalePath(pathname) {
  const p = String(pathname || '/').split(/[?#]/)[0];
  const m = /^\/([a-z]{2})(\/.*)?$/.exec(p);
  if (m && byCode.has(m[1])) {
    const rest = (m[2] || '/').replace(/\/+$/, '') || '/';
    return { lang: m[1], path: rest };
  }
  return { lang: null, path: p.replace(/\/+$/, '') || '/' };
}

// The address of `path` (which may carry #fragment) in `lang`.
// Spanish keeps the unprefixed address; '/' becomes '/fr/'.
function localizedPath(path, lang) {
  const code = baseCode(lang);
  if (!code || code === DEFAULT_LANGUAGE || !byCode.has(code)) return path;
  const [p, frag] = String(path).split('#');
  const bare = p === '' || p === '/' ? '/' : p;
  const out = bare === '/' ? `/${code}/` : `/${code}${bare}`;
  return frag !== undefined ? `${out}#${frag}` : out;
}

function cookieString(lang, { secure = true } = {}) {
  return `${COOKIE}=${baseCode(lang)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`;
}

module.exports = {
  LANGUAGES,
  CODES,
  DEFAULT_LANGUAGE,
  APP_LANGUAGES,
  COOKIE,
  COOKIE_MAX_AGE,
  COUNTRY_LANGUAGE,
  baseCode,
  isSiteLanguage,
  isAppLanguage,
  languageInfo,
  parseAcceptLanguage,
  fromBrowserLanguages,
  fromAcceptLanguage,
  fromCountry,
  readCookie,
  isBot,
  isAppWebView,
  hasAuthParams,
  pickLanguage,
  rootRedirect,
  splitLocalePath,
  localizedPath,
  cookieString,
};
